// src/features/usage/api/usageApi.ts
import { bff } from '@/lib/http/bff';

// ── Types ────────────────────────────────────────────────────────────────

export interface UsageSummary {
  creditBalance:     number;
  lifetimeCredits:   number;
  lifetimeConsumed:  number;
  softLimitDaily:    number;
  hardLimitDaily:    number;
  creditsPerUsd:     number;
  overageRate:       number;
  isMeteringActive:  boolean;
  todayRequests:     number;
  todayRows:         number;
  todayBytes:        number;
  todayErrors:       number;
  todayCostUsd:      number;
  todayPctHardLimit: number;
}

export interface DeviceUsageRow {
  scopeNs:         string;
  scopeKey:        string;
  periodDay:       string;
  opType:          string;
  requestCount:    number;
  rowCount:        number;
  bytesIn:         number;
  errorCount:      number;
  weightedCredits: number;
  firstSeenAt:     string;
  lastSeenAt:      string;
}

export interface DeviceUsageResponse {
  items:   DeviceUsageRow[];
  hasMore: boolean;
}

export interface TimeseriesPoint {
  periodDay:       string;
  requestCount:    number;
  rowCount:        number;
  bytesIn:         number;
  errorCount:      number;
  weightedCredits: number;
  deviceCount:     number;
}

export interface TimeseriesResponse {
  items: TimeseriesPoint[];
}

export interface LedgerEntry {
  ledgerId:     number;
  scopeNs:      string | null;
  scopeKey:     string | null;
  periodDay:    string | null;
  entryType:    string;
  credits:      number;
  requestCount: number | null;
  description:  string | null;
  createdAt:    string;
}

export interface LedgerResponse {
  items:      LedgerEntry[];
  nextCursor: number | null;
}

// ── API calls ────────────────────────────────────────────────────────────

const BASE = '/usage';

export const fetchUsageSummary = (): Promise<UsageSummary> =>
  bff.get<UsageSummary>(`${BASE}/summary`);

export const fetchDeviceUsage = (params?: Record<string, string>): Promise<DeviceUsageResponse> => {
  const qs = params ? `?${new URLSearchParams(params)}` : '';
  return bff.get<DeviceUsageResponse>(`${BASE}/devices${qs}`);
};

export const fetchTimeseries = (params?: Record<string, string>): Promise<TimeseriesResponse> => {
  const qs = params ? `?${new URLSearchParams(params)}` : '';
  return bff.get<TimeseriesResponse>(`${BASE}/timeseries${qs}`);
};

export const fetchLedger = (params?: Record<string, string>): Promise<LedgerResponse> => {
  const qs = params ? `?${new URLSearchParams(params)}` : '';
  return bff.get<LedgerResponse>(`${BASE}/ledger${qs}`);
};

// ── P16 Credit Telemetry — op types + record API ────────────────────────

/** Operation types — mirrors orchestrator metering.OpType constants. */
export const OP_TYPE = {
  INGEST:       'ingest',
  STT:          'stt',
  EDGE_TASK:    'edge_task',
  DEVICE_JOB:   'device_job',
  UPLOAD:       'upload',
  AGENT_THREAD: 'agent_thread',
  VISION:       'vision',
  IMAGE_GEN:    'image_gen',
} as const;

export type OpType = (typeof OP_TYPE)[keyof typeof OP_TYPE];

/** Credit weights — mirrors orchestrator DefaultWeights. */
export const OP_WEIGHTS: Record<OpType, number> = {
  [OP_TYPE.INGEST]:       1,
  [OP_TYPE.STT]:          10,
  [OP_TYPE.EDGE_TASK]:    2,
  [OP_TYPE.DEVICE_JOB]:   2,
  [OP_TYPE.UPLOAD]:       5,
  [OP_TYPE.AGENT_THREAD]: 15,
  [OP_TYPE.VISION]:       8,
  [OP_TYPE.IMAGE_GEN]:    12,
};

export interface RecordUsagePayload {
  scope_ns:   string;
  scope_key:  string;
  op_type:    OpType;
  row_count?: number;
  bytes_in?:  number;
}

export interface RecordUsageResult {
  recorded: boolean;
  op_type:  string;
  weight:   number;
  credits:  number;
}

export const recordUsage = (payload: RecordUsagePayload): Promise<RecordUsageResult> =>
  bff.post<RecordUsageResult, RecordUsagePayload>(`${BASE}/record`, payload);

// ── Cost-transparency header names ──────────────────────────────────────

export const COST_HEADERS = {
  OP_TYPE:   'x-pacmaster-op-type',
  OP_WEIGHT: 'x-pacmaster-op-weight',
  CREDITS:   'x-pacmaster-credits',
  BALANCE:   'x-pacmaster-credit-balance',
  TODAY_PCT: 'x-pacmaster-today-pct',
} as const;
